import { useSuspenseQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import type { PassType } from '@/types';
import { paths, type PassEditorLocationState } from '@/navigation/paths';
import { AppModal, ModalTopBar, QueryBoundary, QueryErrorPlaceholder } from '@/shared/ui';
import { passTypesQueryOptions } from '../api';
import { PassTemplateList } from './PassTemplateList';

interface PassTemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: PassType) => void;
  returnTo?: string;
}

export const PassTemplateSelector: FC<PassTemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  returnTo,
}) => {
  const navigate = useNavigate();

  const handleSelect = (template: PassType) => {
    onSelect(template);
    onClose();
  };

  const handleAdd = () => {
    const state: PassEditorLocationState = { openModal: true, returnTo };
    void navigate(paths.passEditor(), { state });
  };

  return (
    <AppModal
      open={isOpen}
      style={{ backgroundColor: 'var(--shanti-surface)' }}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <ModalTopBar title="Choose pass template" onCancel={onClose} />
      {isOpen ? (
        <QueryBoundary
          errorFallback={(error, reset) => (
            <QueryErrorPlaceholder
              error={error}
              onRetry={reset}
              header="Could not load pass templates"
            />
          )}
        >
          <PassTemplateSelectorContent onSelect={handleSelect} onAdd={handleAdd} />
        </QueryBoundary>
      ) : null}
    </AppModal>
  );
};

interface PassTemplateSelectorContentProps {
  onSelect: (template: PassType) => void;
  onAdd: () => void;
}

const PassTemplateSelectorContent: FC<PassTemplateSelectorContentProps> = ({ onSelect, onAdd }) => {
  const { data: templates } = useSuspenseQuery(passTypesQueryOptions.list());

  return <PassTemplateList templates={templates} onSelect={onSelect} onAdd={onAdd} />;
};
